import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Client } from 'src/users';
import { useClients } from '../hooks/useClients';

type CurrentClient = {
  clientId: string;
  client: Client | null;
  refetch: ReturnType<typeof useClients>['refetch'];
  isFetching: boolean;
  isLoading: boolean;
  isClientPage: boolean;
};

type CurrentClientContextType = {
  clients: Client[];
  isFetching: boolean;
  isLoading: boolean;
  refetch: ReturnType<typeof useClients>['refetch'];
};

const CurrentClientContext = createContext<CurrentClientContextType | undefined>(undefined);

type CurrentClientProviderProps = {
  children: ReactNode;
};

export function CurrentClientProvider({ children }: CurrentClientProviderProps) {
  const { clients, isFetching, isLoading, refetch } = useClients();

  return (
    <CurrentClientContext.Provider
      value={{
        clients,
        isFetching,
        isLoading,
        refetch,
      }}
    >
      {children}
    </CurrentClientContext.Provider>
  );
}

export function useCurrentClientIfAvailable() {
  const context = useContext(CurrentClientContext);
  if (context === undefined) {
    throw new Error('useCurrentClientIfAvailable must be used within a CurrentClientProvider');
  }

  const { clientId: clientIdParam } = useParams<{ clientId: string }>();
  const firstClientId = context.clients[0]?.id;
  const clientId = clientIdParam != null ? clientIdParam : firstClientId;
  const hasClientId = clientId != null && clientId !== 'undefined';
  const client = useMemo(() => {
    if (hasClientId) {
      return context.clients.find(c => c.id === clientId) ?? null;
    }
    return null;
  }, [clientId, context.clients, hasClientId]);

  return {
    ...context,
    isClientPage: clientIdParam != null,
    clientId,
    client,
  };
}

export function useCurrentClient(): CurrentClient {
  const data = useCurrentClientIfAvailable();

  if (hasClientId(data)) {
    return data as CurrentClient;
  }

  throw new Error('Client ID is null');
}

function hasClientId(data: Partial<CurrentClient>): data is CurrentClient {
  return data.clientId != null;
}
